import { useState } from "react";
import { Link } from "react-router-dom";
import { slugify } from "../lib/utils-tv";

function Row({ items, activeName, labelPrefix, buildLink, size, testidPrefix }) {
  // On mobile these filter strips are tall, so collapse them by default and
  // let the user tap to expand. On desktop (md+) they always show inline.
  const [open, setOpen] = useState(false);
  return (
    <div className="max-w-5xl mx-auto px-4 py-2" style={{ fontSize: size }}>
      {/* Mobile-only toggle header: shows the label + current selection */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="md:hidden w-full flex items-center justify-between text-left leading-6"
        data-testid={`${testidPrefix}-nav-toggle`}
        aria-expanded={open}
      >
        <span>
          <span className="text-[#888] mr-1">{labelPrefix}</span>
          {!open && <span className="font-bold underline">{activeName || "all"}</span>}
        </span>
        <span className="text-[#888] ml-2 font-bold">{open ? "[–]" : "[+]"}</span>
      </button>

      {/* Items — collapsed on mobile unless toggled, always shown on desktop */}
      <div
        className={`${open ? "flex" : "hidden"} md:flex flex-wrap items-baseline gap-x-2 gap-y-1 leading-6 mt-1 md:mt-0`}
      >
        <span className="text-[#888] mr-1 hidden md:inline">{labelPrefix}</span>
        {items.map((it, idx) => {
          const slug = it.name === "all" ? "all" : slugify(it.name);
          const isActive = activeName === it.name || (activeName === "all" && it.name === "all");
          const count = typeof it.count === "number" ? it.count : undefined;
          const isEmpty = count === 0 && it.name !== "all";
          const linkClass = isActive
            ? "text-black font-bold underline"
            : isEmpty
            ? "text-[#bbb]"
            : "tv-link";
          return (
            <span key={it.name} className="whitespace-nowrap inline-flex items-baseline">
              <Link
                to={buildLink(it.name)}
                className={linkClass}
                data-testid={`${testidPrefix}-${slug}`}
              >
                {it.name}
                {typeof count === "number" && (
                  <span className={`ml-1 text-[11px] ${isActive ? "text-[#555]" : isEmpty ? "text-[#ddd]" : "text-[#888]"}`}>
                    {count}
                  </span>
                )}
              </Link>
              {idx < items.length - 1 && <span className="text-[#bbb] ml-2">·</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function AssetClassNav({ assetClasses, active, counts }) {
  const items = [
    { name: "all", count: counts?._all },
    ...(assetClasses || []).map((n) => ({ name: n, count: counts?.[n] ?? 0 })),
  ];
  return (
    <div className="border-b border-black bg-white" data-testid="asset-class-nav">
      <Row
        items={items}
        activeName={active}
        labelPrefix="asset"
        buildLink={(name) => (name === "all" ? "/" : `/c/${slugify(name)}`)}
        size={13}
        testidPrefix="asset"
      />
    </div>
  );
}

export function FunctionNav({ categories, active, counts, currentAsset }) {
  const items = [
    { name: "all", count: counts?._all },
    ...(categories || []).map((n) => ({ name: n, count: counts?.[n] ?? 0 })),
  ];
  const buildLink = (fn) => {
    const hasAsset = currentAsset && currentAsset !== "all";
    if (fn === "all") {
      return hasAsset ? `/c/${slugify(currentAsset)}` : "/";
    }
    if (hasAsset) {
      return `/c/${slugify(currentAsset)}/${slugify(fn)}`;
    }
    return `/f/${slugify(fn)}`;
  };
  return (
    <div className="border-b border-[#ddd] bg-[#fafafa]" data-testid="function-nav">
      <Row
        items={items}
        activeName={active}
        labelPrefix="function"
        buildLink={buildLink}
        size={12}
        testidPrefix="fn"
      />
    </div>
  );
}

export function SectorNav({ sectors, active, counts }) {
  const items = [
    { name: "all", count: counts?._all },
    ...(sectors || []).map((n) => ({ name: n, count: counts?.[n] ?? 0 })),
  ];
  return (
    <div className="border-b border-[#ddd] bg-[#fafafa]" data-testid="sector-nav">
      <Row
        items={items}
        activeName={active}
        labelPrefix="sector"
        buildLink={(name) => (name === "all" ? "/" : `/sector/${slugify(name)}`)}
        size={12}
        testidPrefix="sector"
      />
    </div>
  );
}
